import React, { useEffect } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    Box,
    Button
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    Cancel as CancelIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    AccessTime as PendingIcon
} from "@mui/icons-material";

const ScanProgressList = ({ scans, loading, onRefreshScan, onCancelScan, onViewProducts }) => {
    useEffect(() => {
        if (!scans || scans.length === 0) return;
        
        const activeScans = scans.filter(scan => 
            scan.status === 'pending' || scan.status === 'scanning'
        );

        const intervals = [];

        activeScans.forEach(scan => {
            const interval = setInterval(() => {
                onRefreshScan(scan._id);
            }, 5000);
            intervals.push(interval);
        });

        return () => {
            intervals.forEach(clearInterval);
        };
    }, [scans]);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckIcon color="success" />;
            case 'failed':
                return <ErrorIcon color="error" />;
            case 'cancelled':
                return <CancelIcon color="disabled" />;
            case 'pending':
                return <PendingIcon color="info" />;
            case 'scanning':
                return <RefreshIcon color="primary" className="rotating" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            case 'cancelled':
                return 'default';
            case 'pending':
                return 'info';
            case 'scanning':
                return 'primary';
            default:
                return 'default';
        }
    };

    const formatDuration = (startedAt, completedAt) => {
        if (!startedAt) return 'N/A';
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const durationSec = Math.floor((end - start) / 1000);
        
        if (durationSec < 60) return `${durationSec}s`;
        if (durationSec < 3600) return `${Math.floor(durationSec / 60)}m ${durationSec % 60}s`;
        return `${Math.floor(durationSec / 3600)}h ${Math.floor((durationSec % 3600) / 60)}m`;
    };

    const handleCancel = (scanId) => {
        if (window.confirm('Are you sure you want to cancel this scan?')) {
            onCancelScan(scanId);
        }
    };

    if (!scans || scans.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    No scans yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Click "New Scan" to start scanning a website
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Domain</TableCell>
                            <TableCell>Progress</TableCell>
                            <TableCell align="center">Pages</TableCell>
                            <TableCell align="center">Products</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scans && scans.map((scan) => (
                            <TableRow key={scan._id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusIcon(scan.status)}
                                        <Chip
                                            label={scan.status.toUpperCase()}
                                            size="small"
                                            color={getStatusColor(scan.status)}
                                        />
                                    </Box>
                                </TableCell>
                                
                                <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                        {scan.domain}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" noWrap>
                                        {scan.target_url}
                                    </Typography>
                                </TableCell>
                                
                                <TableCell>
                                    <Box sx={{ minWidth: 200 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption">
                                                {scan.progress?.percent_complete || 0}%
                                            </Typography>
                                            {scan.progress?.current_page && (
                                                <Tooltip title={scan.progress.current_page}>
                                                    <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 150 }}>
                                                        {scan.progress.current_page.split('/').pop()}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={scan.progress?.percent_complete || 0}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>
                                </TableCell>
                                
                                <TableCell align="center">
                                    <Typography variant="body2">
                                        {scan.progress?.pages_scanned || 0}
                                    </Typography>
                                </TableCell>
                                
                                <TableCell align="center">
                                    <Chip
                                        label={scan.progress?.products_found || 0}
                                        size="small"
                                        color={scan.progress?.products_found > 0 ? 'primary' : 'default'}
                                    />
                                </TableCell>
                                
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatDuration(scan.started_at, scan.completed_at)}
                                    </Typography>
                                </TableCell>
                                
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        {(scan.status === 'pending' || scan.status === 'scanning') && (
                                            <Tooltip title="Cancel Scan">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleCancel(scan._id)}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        
                                        {scan.status === 'completed' && scan.results?.total_products > 0 && (
                                            <Tooltip title="View Products">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => onViewProducts(scan.domain)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        
                                        <Tooltip title="Refresh Status">
                                            <IconButton
                                                size="small"
                                                onClick={() => onRefreshScan(scan._id)}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <style>
                {`
                    @keyframes rotate {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .rotating {
                        animation: rotate 2s linear infinite;
                    }
                `}
            </style>
        </Paper>
    );
};

export default ScanProgressList;
